import { useState } from "react";
import { Loader2, Sparkles } from "lucide-react";
import { onboardingApi, completeOnboarding } from "../../api";

type WizardStatus = "idle" | "loading" | "done" | "error";

interface OnboardingWizardProps {
  agentName?: string;
  onComplete: () => void;
}

export default function OnboardingWizard({ agentName = "Brian", onComplete }: OnboardingWizardProps) {
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState<WizardStatus>("idle");
  const [errorMsg, setErrorMsg] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!description.trim()) return;
    setStatus("loading");
    setErrorMsg("");
    try {
      await onboardingApi.generateDashboard(description.trim());
      setStatus("done");
      // Small delay so user sees the success state before the modal closes
      setTimeout(() => onComplete(), 800);
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setErrorMsg(detail ?? "Something went wrong. Please try again.");
      setStatus("error");
    }
  }

  async function handleSkip() {
    try {
      await completeOnboarding();
    } catch {
      // best-effort — don't block the user
    }
    onComplete();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="rounded-t-2xl bg-[#1D345F] px-6 py-5 text-white">
          <div className="flex items-center gap-2">
            <Sparkles size={20} className="text-blue-300" />
            <h2 className="text-lg font-bold">Welcome to {agentName}</h2>
          </div>
          <p className="mt-1 text-sm text-blue-200">
            Tell {agentName} what matters to you and get a personalised starter dashboard.
          </p>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6">
          <label
            htmlFor="onboarding-description"
            className="mb-2 block text-sm font-medium text-gray-700"
          >
            What's most important to you?
          </label>
          <textarea
            id="onboarding-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={`e.g. "I want to track daily GGR by sport, see our biggest liabilities, and monitor player activity."`}
            rows={4}
            maxLength={2000}
            disabled={status === "loading" || status === "done"}
            className="w-full resize-none rounded-lg border border-gray-300 p-3 text-sm text-gray-800 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50"
          />
          <p className="mt-1 text-right text-xs text-gray-400">
            {description.length}/2000
          </p>

          {status === "error" && (
            <p className="mt-2 text-sm text-red-600">{errorMsg}</p>
          )}

          {status === "done" && (
            <p className="mt-2 text-sm font-medium text-green-600">
              Dashboard created! Loading your reports...
            </p>
          )}

          <div className="mt-4 flex items-center justify-between">
            <button
              type="button"
              onClick={handleSkip}
              disabled={status === "loading" || status === "done"}
              className="text-sm text-gray-400 hover:text-gray-600 disabled:opacity-50"
            >
              Skip for now
            </button>
            <button
              type="submit"
              disabled={!description.trim() || status === "loading" || status === "done"}
              className="flex items-center gap-2 rounded-lg bg-[#1D345F] px-5 py-2 text-sm font-semibold text-white hover:bg-[#162848] disabled:opacity-50"
            >
              {status === "loading" ? (
                <>
                  <Loader2 size={15} className="animate-spin" />
                  Building dashboard...
                </>
              ) : (
                "Create my dashboard"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
